import { Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import {
  Auth,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  UserCredential,
  User,
  onAuthStateChanged,
  GoogleAuthProvider,
  authState,
  signInWithCustomToken,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { SignInWithApple, SignInWithAppleOptions } from '@capacitor-community/apple-sign-in';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import {
  signInWithCredential,
  signInWithPopup,
  OAuthProvider,
} from 'firebase/auth';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';

import { isUserEqual } from './login.utils';
import { CoreServicesModule } from '../../core-services.module';

const isEmailVerified = (userCredential: UserCredential): boolean => userCredential.user.emailVerified;
const errorHandler = (error: FirebaseError) => error.message.split('Firebase: ').pop() || 'An Error Occurred, Please try again later.';

@Injectable({
  providedIn: CoreServicesModule,
})
export class AuthenticationService {

  readonly errorHandler = errorHandler;
  readonly isEmailVerified = isEmailVerified;
  readonly user$ = new BehaviorSubject<User | null>(null);

  constructor(
    private readonly auth: Auth,
    private readonly router: Router,
  ) {
    onAuthStateChanged(this.auth, this.user$);
  }

  async sendEmailVerification() {
    const user = await this.user$.pipe(first()).toPromise();
    if (!user) {
      return;
    }
    return sendEmailVerification(user);
  }

  async registration(displayName: string, email: string, password: string): Promise<void> {
    const credential = await createUserWithEmailAndPassword(this.auth, email, password);
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    await sendEmailVerification(credential.user);
    await signOut(this.auth);
  }

  async loginWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return await signInWithEmailAndPassword(this.auth, email, password);
  }

  async forgotPassword(email: string): Promise<void> {
    return await sendPasswordResetEmail(this.auth, email);
  }

  async loginWithCustomToken(token: string): Promise<UserCredential> {
    return await signInWithCustomToken(this.auth, token);
  }

  async signUpWithGoogle(): Promise<any> {
    const googleUser = await GoogleAuth.signIn();
    const firebaseUser = await authState(this.auth).pipe(first()).toPromise();
    if (!firebaseUser || !isUserEqual(googleUser, firebaseUser)) {
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      return signInWithCredential(this.auth, credential);
    }
  }

  async loginWithGoogle(): Promise<any> {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(this.auth, provider);
    return userCredential.user;
  }

  async signUpWithApple() {
    if (Capacitor.getPlatform() === 'web') {
      return this.signUpWithAppleWeb();
    } else {
      return this.signUpWithAppleMobile();
    }
  }

  async signUpWithAppleWeb() {
    const provider = new OAuthProvider('apple.com');
    return await signInWithPopup(this.auth, provider);
  }

  async signUpWithAppleMobile() {
    const options: SignInWithAppleOptions = {
      clientId: 'it.centrosistemi.system.monitoring',
      redirectURI: 'https://azzurroagent-qfmyjb.firebaseapp.com/__/auth/handler',
      scopes: 'email name',
      state: '12345',
    };

    const signInWithAppleResponse = await SignInWithApple.authorize(options);
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({ idToken: signInWithAppleResponse.response.identityToken });
    return await signInWithCredential(this.auth, credential);
  }

  async deleteUserAccount() {
    const user = await this.user$.pipe(first()).toPromise();
    if (!user) {
      throw new Error('User not found');
    }
    return user.delete();
  }

  async signOut(exit = false): Promise<void> {
    // 1. Sign out from Firebase - handles all auth providers including Google
    // Note: GoogleAuth.signOut() is intentionally NOT called because it causes
    // NullPointerException crash on Android when GoogleSignInClient is not initialized
    // (e.g., when user logged in via web or in a previous session).
    // Firebase signOut is sufficient to clear the session.
    try {
      await signOut(this.auth);
    } catch (e) {
      console.error('[AuthService] Firebase signOut failed:', e);
    }

    // 2. Force clear local user state
    this.user$.next(null);

    if (exit) {
      // 3. Small delay to ensure auth state propagates to guards
      await new Promise(resolve => setTimeout(resolve, 150));
      await this.router.navigate(['/onboarding'], { replaceUrl: true });
    }
  }
}
